import { createContext, PropsWithChildren, useContext, useEffect, useId, useRef } from 'react';

interface DurationVitalContextValue {
    vitalName: string;
    startedAt: number;
    instanceId: string;
    context?: Record<string, unknown>;
}

const DurationVitalContext = createContext<DurationVitalContextValue | null>(null);

/** Dedupes StrictMode effect re-runs for the same provider instance. */
const reportedKeys = new Set<string>();

interface DurationVitalProviderProps extends PropsWithChildren {
    vitalName: string;
    context?: Record<string, unknown>;
}

export const DurationVitalProvider = ({
    vitalName,
    context,
    children,
}: DurationVitalProviderProps) => {
    const startedAtRef = useRef(Date.now());
    const instanceId = useId();

    return (
        <DurationVitalContext.Provider
            value={{
                vitalName,
                startedAt: startedAtRef.current,
                instanceId,
                context,
            }}
        >
            {children}
        </DurationVitalContext.Provider>
    );
};

export interface ReportDurationVitalProps {
    /** Merged over the provider's context; lets the reporting site label which state it settled on. */
    context?: Record<string, unknown>;
}

export const ReportDurationVital = ({ context: reportContext }: ReportDurationVitalProps) => {
    const vital = useContext(DurationVitalContext);

    // Held in a ref so an inline context object can't re-run the effect and postpone the report.
    const reportContextRef = useRef(reportContext);
    reportContextRef.current = reportContext;

    useEffect(() => {
        if (!vital) {
            return;
        }

        const { vitalName, startedAt, instanceId, context } = vital;
        const reportKey = `${vitalName}:${instanceId}`;

        if (reportedKeys.has(reportKey)) {
            return;
        }

        let cancelled = false;
        let outerFrameId = 0;
        let innerFrameId = 0;

        // useEffect runs after React commits DOM updates, but before the browser paints.
        // The first rAF waits until the next paint cycle; the second ensures we report
        // after that paint completes so the vital reflects visible content, not just mount.
        outerFrameId = requestAnimationFrame(() => {
            innerFrameId = requestAnimationFrame(() => {
                if (cancelled || reportedKeys.has(reportKey)) {
                    return;
                }

                reportedKeys.add(reportKey);

                window.DD_RUM?.addDurationVital?.(vitalName, {
                    startTime: startedAt,
                    duration: Date.now() - startedAt,
                    context: reportContextRef.current
                        ? { ...context, ...reportContextRef.current }
                        : context,
                });
            });
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(outerFrameId);
            cancelAnimationFrame(innerFrameId);
        };
    }, [vital]);

    return null;
};

/** Place after loading/setup gates so the vital excludes interim null/loader states. */
export const DurationVitalGate = ({
    context,
    children,
}: PropsWithChildren<ReportDurationVitalProps>) => (
    <>
        <ReportDurationVital context={context} />
        {children}
    </>
);
